import {
  INTERCEPTInternalServerError,
  INTERCEPTServiceUnavailable,
  INTERCEPTConflict,
  INTERCEPTNotFound,
  INTERCEPTForbidden,
  INTERCEPTRequestTimeout,
} from 'app/core/interceptors/contexts';
import { HttpClient, HttpParams, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce, shareReplay } from 'rxjs/operators';
import { ServiceBase } from './service.base';
import {
  FieldNotebookChanges,
  FieldNotebookTasksResult,
  FieldNotebookCrop,
  FieldNotebookEntry,
  FieldNotebookListResult,
  FieldNotebookPage,
  FieldNotebookProductSuggestion,
  NotebookBbchCatalog,
} from 'app/core/interface/field-notebook';

export const NOTEBOOK_PAGE_SIZE = 500;
export const NOTEBOOK_MAX_PAGES = 100;

@Injectable({ providedIn: 'root' })
export class FieldNotebookService extends ServiceBase {
  private readonly http = inject(HttpClient);
  private bbchCatalog$?: Observable<NotebookBbchCatalog>;

  /** Reads every page, including empty Cosmos pages carrying a continuation token. */
  list(from: string, to: string): Observable<FieldNotebookListResult> {
    const page = (token?: string) => {
      let params = new HttpParams()
        .set('from', from)
        .set('to', to)
        .set('limit', NOTEBOOK_PAGE_SIZE);
      if (token) {
        params = params.set('continuationToken', token);
      }
      return this.http.get<FieldNotebookPage>(
        `${this.UrlServiceV1}field-notebook`,
        { ...this.obterAuthHeader(), params },
      );
    };
    return page().pipe(
      expand((result, index) => {
        if (result.continuationToken && index + 1 < NOTEBOOK_MAX_PAGES) {
          return page(result.continuationToken);
        }
        return EMPTY;
      }, 1),
      reduce(
        (all, result) => ({
          items: [...all.items, ...result.items],
          truncated: Boolean(result.continuationToken),
        }),
        { items: [], truncated: false } as FieldNotebookListResult,
      ),
      map((result) => ({
        ...result,
        items: [
          ...new Map(result.items.map((item) => [item.id, item])).values(),
        ],
      })),
    );
  }
  get(id: string): Observable<FieldNotebookEntry> {
    return this.http.get<FieldNotebookEntry>(this.entryUrl(id), {
      ...this.obterAuthHeader(),
      context: this.entryContext(),
    });
  }
  create(changes: FieldNotebookChanges): Observable<FieldNotebookEntry> {
    return this.http.post<FieldNotebookEntry>(
      `${this.UrlServiceV1}field-notebook`,
      changes,
      { ...this.obterAuthHeader(), context: this.entryContext(true) },
    );
  }
  update(
    id: string,
    changes: FieldNotebookChanges,
    correction = false,
  ): Observable<FieldNotebookEntry> {
    return this.http.put<FieldNotebookEntry>(
      this.entryUrl(id),
      { changes, correction },
      { ...this.obterAuthHeader(), context: this.entryContext(true) },
    );
  }
  confirm(id: string): Observable<FieldNotebookEntry> {
    return this.http.post<FieldNotebookEntry>(
      `${this.entryUrl(id)}/confirm`,
      {},
      { ...this.obterAuthHeader(), context: this.entryContext(true) },
    );
  }
  delete(id: string): Observable<void> {
    return this.http.delete<void>(this.entryUrl(id), {
      ...this.obterAuthHeader(),
      context: this.entryContext(true),
    });
  }
  tasks(from: string, to: string): Observable<FieldNotebookTasksResult> {
    return this.http.get<FieldNotebookTasksResult>(
      `${this.UrlServiceV1}field-notebook-tasks`,
      {
        ...this.obterAuthHeader(),
        params: new HttpParams().set('from', from).set('to', to),
        context: this.importContext(),
      },
    );
  }
  generate(
    taskId: string,
    rebuildDeleted = false,
  ): Observable<FieldNotebookEntry> {
    return this.http.post<FieldNotebookEntry>(
      `${this.UrlServiceV1}field-notebook-generate`,
      {},
      {
        ...this.obterAuthHeader(),
        params: new HttpParams()
          .set('taskId', taskId)
          .set('rebuildDeleted', rebuildDeleted),
        context: this.importContext(),
      },
    );
  }
  products(): Observable<FieldNotebookProductSuggestion[]> {
    return this.http.get<FieldNotebookProductSuggestion[]>(
      `${this.UrlServiceV1}field-notebook-products`,
      this.obterAuthHeader(),
    );
  }
  crops(): Observable<FieldNotebookCrop[]> {
    return this.http.get<FieldNotebookCrop[]>(
      `${this.UrlServiceV1}field-notebook-crops`,
      this.obterAuthHeader(),
    );
  }
  /** Shares the static four-language catalog between the table and every opened panel. Errors can be retried. */
  bbch(): Observable<NotebookBbchCatalog> {
    if (!this.bbchCatalog$) {
      this.bbchCatalog$ = this.http
        .get<NotebookBbchCatalog>(`${this.UrlServiceV1}field-notebook-crops`, {
          ...this.obterAuthHeader(),
          params: new HttpParams().set('catalog', 'bbch'),
        })
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.bbchCatalog$;
  }
  /** The entry UI handles conflicts and keeps unsaved forms on write failures. */
  private entryContext(write = false): HttpContext {
    const context = new HttpContext()
      .set(INTERCEPTConflict, false)
      .set(INTERCEPTNotFound, false);
    if (write) {
      context
        .set(INTERCEPTInternalServerError, false)
        .set(INTERCEPTServiceUnavailable, false);
    }
    return context;
  }
  /** Import controls handle recoverable failures and show a single batch summary. */
  private importContext(): HttpContext {
    return new HttpContext()
      .set(INTERCEPTInternalServerError, false)
      .set(INTERCEPTServiceUnavailable, false)
      .set(INTERCEPTConflict, false)
      .set(INTERCEPTNotFound, false)
      .set(INTERCEPTForbidden, false)
      .set(INTERCEPTRequestTimeout, false);
  }
  private entryUrl(id: string): string {
    return `${this.UrlServiceV1}field-notebook/${encodeURIComponent(id)}`;
  }
}
